"use client";

import { useActionState, useState, useMemo } from "react";
import { reserveListing, type ReserveState } from "@/lib/booking-actions";
import { formatPrice, nightsBetween } from "@/lib/format";
import { computeTouristTax } from "@/lib/tourist-tax";
import SubmitButton from "./SubmitButton";
import DateRangePicker from "./DateRangePicker";

type Props = {
  listingId: string;
  pricePerNight: number;
  cleaningFee: number;
  currency: string;
  maxGuests: number;
  defaultCheckIn?: string;
  defaultCheckOut?: string;
  defaultGuests?: number;
  /** Full party breakdown forwarded from the search page so the
   *  Adults / Children / Infants / Pets steppers open at the same
   *  values the guest already chose. */
  defaultAdults?: number;
  defaultChildren?: number;
  defaultInfants?: number;
  defaultPets?: number;
  blockedRanges?: { from: string; to: string }[];
  touristTaxBasis?: string | null;
  touristTaxAmount?: number | null;
  touristTaxMaxNights?: number | null;
  touristTaxMinAge?: number | null;
  touristTaxLegacyPerGuestPerNight?: number | null;
  /** Greek Climate Resilience Fee category — auto-calc per night based
   *  on the dates the guest picks (high vs low season). */
  climateResilienceCategory?: string | null;
  // Booking constraints
  minNights?: number;
  maxNights?: number | null;
  weeklyDiscountBps?: number;
  monthlyDiscountBps?: number;
  hasHouseRules?: boolean;
  // Payment methods this listing accepts. If more than one is true the
  // form shows a radio picker; if only one is true, that one is used
  // silently. The form re-computes the price preview (zero fees for
  // bank-transfer and cash) based on the current selection so the guest
  // sees the right total before clicking Reserve.
  acceptsStripe?: boolean;
  acceptsBankTransfer?: boolean;
  acceptsCash?: boolean;
};

export default function ReserveForm({
  listingId,
  pricePerNight,
  cleaningFee,
  currency,
  maxGuests,
  defaultCheckIn,
  defaultCheckOut,
  defaultGuests,
  defaultAdults,
  defaultChildren,
  defaultInfants,
  defaultPets,
  blockedRanges = [],
  touristTaxBasis = null,
  touristTaxAmount = null,
  touristTaxMaxNights = null,
  touristTaxMinAge = null,
  touristTaxLegacyPerGuestPerNight = null,
  climateResilienceCategory = null,
  minNights = 1,
  maxNights = null,
  weeklyDiscountBps = 0,
  monthlyDiscountBps = 0,
  hasHouseRules = false,
  acceptsStripe = true,
  acceptsBankTransfer = false,
  acceptsCash = false,
}: Props) {
  const [state, action] = useActionState<ReserveState, FormData>(reserveListing, {});
  const [paymentMethod, setPaymentMethod] = useState<"stripe" | "bank_transfer" | "cash">(
    // Default order: Stripe > bank transfer > cash. Stripe gives platform
    // protection, so prefer it when available.
    acceptsStripe ? "stripe" : acceptsBankTransfer ? "bank_transfer" : "cash",
  );
  const [checkIn, setCheckIn] = useState(defaultCheckIn ?? "");
  const [checkOut, setCheckOut] = useState(defaultCheckOut ?? "");
  // Adults/Children/Infants/Pets split — when explicit defaults arrive
  // from the search page, use those; otherwise fall back to the legacy
  // `guests` total as adults. `guests` stays in sync with
  // adults + children so the rest of the pricing flow doesn't change.
  const initAdults = defaultAdults && defaultAdults > 0
    ? defaultAdults
    : Math.max(1, defaultGuests ?? 2);
  const initChildren = defaultChildren ?? 0;
  const initInfants = defaultInfants ?? 0;
  const initPets = defaultPets ?? 0;
  const [adults, setAdults] = useState(initAdults);
  const [children, setChildren] = useState(initChildren);
  const [infants, setInfants] = useState(initInfants);
  const [partyPets, setPartyPets] = useState(initPets);
  const [guests, setGuests] = useState(initAdults + initChildren);
  const [travellerNotes, setTravellerNotes] = useState("");
  const [whoOpen, setWhoOpen] = useState(false);

  // Summary line shown when the Who section is collapsed. Matches the
  // Airbnb pill ("4 guests, 1 infant") so the user can see the current
  // selection at a glance.
  const totalAdultChild = adults + children;
  const whoSummary = (() => {
    if (totalAdultChild === 0) return "Add guests";
    const bits = [`${totalAdultChild} guest${totalAdultChild === 1 ? "" : "s"}`];
    if (infants > 0) bits.push(`${infants} infant${infants === 1 ? "" : "s"}`);
    if (partyPets > 0) bits.push(`${partyPets} pet${partyPets === 1 ? "" : "s"}`);
    return bits.join(", ");
  })();

  // Match server-side default in src/lib/stripe.ts (GUEST_SERVICE_FEE_BPS=300)
  const guestFeeBps = 300;

  const breakdown = useMemo(() => {
    const nights = checkIn && checkOut ? nightsBetween(checkIn, checkOut) : 0;
    if (nights <= 0) return null;
    const subtotal = pricePerNight * nights;
    const tax = computeTouristTax(
      {
        basis: touristTaxBasis,
        amount: touristTaxAmount,
        maxNights: touristTaxMaxNights,
        minAge: touristTaxMinAge,
        legacyPerGuestPerNight: touristTaxLegacyPerGuestPerNight,
        climateResilienceCategory,
      },
      guests,
      nights,
      subtotal,
      { checkIn, checkOut },
    );
    // Length-of-stay discount preview
    let discount = 0;
    if (nights >= 28 && monthlyDiscountBps > 0) {
      discount = Math.round((subtotal * monthlyDiscountBps) / 10_000);
    } else if (nights >= 7 && weeklyDiscountBps > 0) {
      discount = Math.round((subtotal * weeklyDiscountBps) / 10_000);
    }
    const discountedSubtotal = subtotal - discount;
    const stayAmount = discountedSubtotal + cleaningFee;
    // Bank transfer and cash skip Stripe entirely — no service fee.
    // Mirrors the server-side rule in src/lib/pricing.ts.
    const isOffPlatform = paymentMethod === "bank_transfer" || paymentMethod === "cash";
    const serviceFee = isOffPlatform
      ? 0
      : Math.round((stayAmount * guestFeeBps) / 10_000);
    const total = stayAmount + tax + serviceFee;
    return { nights, subtotal: discountedSubtotal, grossSubtotal: subtotal, discount, tax, serviceFee, total };
  }, [
    checkIn,
    checkOut,
    pricePerNight,
    cleaningFee,
    guests,
    touristTaxBasis,
    touristTaxAmount,
    touristTaxMaxNights,
    touristTaxMinAge,
    touristTaxLegacyPerGuestPerNight,
    weeklyDiscountBps,
    monthlyDiscountBps,
    paymentMethod,
  ]);

  // Validation hint for min/max stay
  const stayValidation = useMemo(() => {
    if (!checkIn || !checkOut) return null;
    const nights = nightsBetween(checkIn, checkOut);
    if (nights <= 0) return null;
    if (nights < minNights) {
      return `Minimum stay: ${minNights} night${minNights === 1 ? "" : "s"}`;
    }
    if (maxNights && nights > maxNights) {
      return `Maximum stay: ${maxNights} nights`;
    }
    return null;
  }, [checkIn, checkOut, minNights, maxNights]);

  return (
    <form
      action={action}
      className="space-y-3"
      onChange={(e) => {
        const fd = new FormData(e.currentTarget);
        setCheckIn(String(fd.get("checkIn") ?? ""));
        setCheckOut(String(fd.get("checkOut") ?? ""));
        const g = Number(fd.get("guests"));
        if (!Number.isNaN(g) && g > 0) setGuests(g);
      }}
    >
      <input type="hidden" name="listingId" value={listingId} />
      <input type="hidden" name="paymentMethod" value={paymentMethod} />
      <DateRangePicker
        defaultStart={defaultCheckIn}
        defaultEnd={defaultCheckOut}
        disabledRanges={blockedRanges}
        numberOfMonths={1}
      />
      {/* "Who's coming" — collapsed by default, shows a summary pill
          like "4 guests, 1 infant" with a chevron. Click to expand
          the full Adults / Children / Infants / Pets stepper UI. */}
      <div className="border border-neutral-200 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setWhoOpen((v) => !v)}
          className="w-full text-left px-3 py-2.5 flex items-center justify-between gap-2 hover:bg-neutral-50"
          aria-expanded={whoOpen}
        >
          <span>
            <span className="block text-xs font-semibold uppercase tracking-wide text-neutral-600">
              Guests
            </span>
            <span
              className={`block text-sm mt-0.5 ${totalAdultChild === 0 ? "text-neutral-400" : "text-neutral-900"}`}
            >
              {whoSummary}
            </span>
          </span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.6}
            style={{
              transition: "transform .15s",
              transform: whoOpen ? "rotate(180deg)" : "none",
              color: "var(--ink-3)",
            }}
          >
            <path d="M3 6l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        {whoOpen ? (
          <div className="border-t border-neutral-200 px-3 py-2">
            <ReserveStepper
              title="Adults"
              sub="Ages 13 or above"
              value={adults}
              min={1}
              onChange={(v) => {
                setAdults(v);
                setGuests(v + children);
              }}
            />
            <ReserveStepper
              title="Children"
              sub="Ages 2 — 12"
              value={children}
              min={0}
              onChange={(v) => {
                setChildren(v);
                setGuests(adults + v);
              }}
            />
            <ReserveStepper
              title="Infants"
              sub="Under 2"
              value={infants}
              min={0}
              onChange={setInfants}
            />
            <ReserveStepper
              title="Pets"
              sub="Check the listing's pet policy"
              value={partyPets}
              min={0}
              onChange={setPartyPets}
              last
            />
            {adults + children > maxGuests ? (
              <p className="mt-2 text-xs text-rose-600">
                This listing fits up to {maxGuests} guest{maxGuests === 1 ? "" : "s"} (adults + children).
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
      <input type="hidden" name="guests" value={adults + children} />
      {/* Notes the host sees on the request — combined with the
          structured party breakdown into the coTravellers field. */}
      <label className="block">
        <span className="text-xs font-semibold uppercase tracking-wide text-neutral-600">
          Anything the host should know? <span className="font-normal text-neutral-400 normal-case">(optional)</span>
        </span>
        <textarea
          name="travellerNotes"
          rows={3}
          maxLength={500}
          value={travellerNotes}
          onChange={(e) => setTravellerNotes(e.target.value)}
          placeholder="e.g. Arriving late, travelling with a service animal, working remotely…"
          className="input mt-1 w-full"
        />
      </label>
      {/* coTravellers: party breakdown + notes, serialised so the
          host sees both on the booking page. */}
      <input
        type="hidden"
        name="coTravellers"
        value={JSON.stringify({
          adults,
          children,
          infants,
          pets: partyPets,
          notes: travellerNotes,
        })}
      />

      {(() => {
        // Count enabled methods; show picker only when there's a choice.
        const enabled = [acceptsStripe, acceptsBankTransfer, acceptsCash].filter(Boolean).length;
        if (enabled < 2) return null;
        const RadioRow = ({
          value,
          title,
          subtitle,
        }: {
          value: "stripe" | "bank_transfer" | "cash";
          title: React.ReactNode;
          subtitle: React.ReactNode;
        }) => (
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              name="paymentMethodChoice"
              value={value}
              checked={paymentMethod === value}
              onChange={() => setPaymentMethod(value)}
              className="mt-1 w-4 h-4 accent-rose-600"
            />
            <span>
              <span className="font-medium">{title}</span>
              <span className="block text-xs text-neutral-500 mt-0.5">{subtitle}</span>
            </span>
          </label>
        );
        return (
          <fieldset className="border-t border-neutral-200 pt-3">
            <legend className="text-xs font-semibold uppercase tracking-wide text-neutral-600 mb-2">
              Payment method
            </legend>
            <div className="space-y-2 text-sm">
              {acceptsStripe ? (
                <RadioRow
                  value="stripe"
                  title="Card, Klarna or Apple Pay (recommended)"
                  subtitle="Pay now via Stripe. Charged only if the host accepts. Full platform protection. Klarna lets you pay in 3 interest-free instalments."
                />
              ) : null}
              {acceptsBankTransfer ? (
                <RadioRow
                  value="bank_transfer"
                  title="Bank transfer (no service fee)"
                  subtitle="You pay the host's IBAN directly within 48h. No platform protection — room2let can't mediate disputes."
                />
              ) : null}
              {acceptsCash ? (
                <RadioRow
                  value="cash"
                  title="Cash on arrival (no service fee)"
                  subtitle="The host approves your reservation up front; you pay cash at check-in. No platform protection. Bring close to the exact amount."
                />
              ) : null}
            </div>
          </fieldset>
        );
      })()}

      <div className="border-t border-neutral-200 pt-3 text-sm space-y-1.5 text-neutral-700">
        {breakdown ? (
          <>
            <div className="flex justify-between">
              <span>{formatPrice(pricePerNight, currency)} × {breakdown.nights} night{breakdown.nights === 1 ? "" : "s"}</span>
              <span>{formatPrice(breakdown.grossSubtotal, currency)}</span>
            </div>
            {breakdown.discount > 0 ? (
              <div className="flex justify-between text-emerald-700">
                <span>{breakdown.nights >= 28 ? "Monthly" : "Weekly"} discount</span>
                <span>−{formatPrice(breakdown.discount, currency)}</span>
              </div>
            ) : null}
            {cleaningFee > 0 ? (
              <div className="flex justify-between">
                <span>Cleaning fee</span>
                <span>{formatPrice(cleaningFee, currency)}</span>
              </div>
            ) : null}
            {breakdown.tax > 0 ? (
              <div className="flex justify-between">
                <span>Tourist tax</span>
                <span>{formatPrice(breakdown.tax, currency)}</span>
              </div>
            ) : null}
            {breakdown.serviceFee > 0 ? (
              <div className="flex justify-between">
                <span title="Covers payment processing, support, and platform operation">
                  Service fee
                </span>
                <span>{formatPrice(breakdown.serviceFee, currency)}</span>
              </div>
            ) : null}
            <div className="flex justify-between border-t border-neutral-200 pt-2 mt-2 font-semibold text-neutral-900">
              <span>Total</span>
              <span>{formatPrice(breakdown.total, currency)}</span>
            </div>
          </>
        ) : (
          <div className="text-neutral-500">
            Pick dates to see the total.
            {minNights > 1 ? ` Minimum stay ${minNights} nights.` : ""}
          </div>
        )}
      </div>

      {stayValidation ? (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-300 rounded px-2 py-1.5">
          {stayValidation}
        </p>
      ) : null}

      {hasHouseRules ? (
        <label className="flex items-start gap-3 text-sm text-neutral-700 mt-2">
          <input
            type="checkbox"
            name="acceptHouseRules"
            value="1"
            required
            // Bumped from default ~16px to a 20px touch target so it's
            // tappable on mobile without zooming.
            className="mt-0.5 w-5 h-5 accent-rose-600 shrink-0"
          />
          <span className="leading-snug">
            I&apos;ve read and agree to the host&apos;s house rules.
          </span>
        </label>
      ) : null}

      {state.error ? (
        <p className="text-sm text-rose-600">{state.error}</p>
      ) : null}

      <SubmitButton
        pendingLabel="Reserving…"
        // 48px min-height passes iOS tap target guidelines comfortably.
        className="w-full bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white rounded-lg py-3 min-h-[48px] text-base font-semibold"
      >
        Reserve
      </SubmitButton>
      <p className="text-xs text-neutral-500 text-center">
        You won&apos;t be charged until the host accepts.
      </p>

      {/* font-size 16px on inputs prevents iOS Safari from zooming on focus.
          Bumped padding for a 44px+ tap target. */}
      <style>{`.input{width:100%;border:1px solid #e5e5e5;border-radius:0.5rem;padding:0.625rem 0.75rem;font-size:1rem;outline:none;background:white;min-height:44px}.input:focus{border-color:#e11d48}`}</style>
    </form>
  );
}

function ReserveStepper({
  title,
  sub,
  value,
  min,
  onChange,
  last,
}: {
  title: string;
  sub: string;
  value: number;
  min: number;
  onChange: (v: number) => void;
  last?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between gap-3 py-2 ${last ? "" : "border-b border-neutral-100"}`}
    >
      <div>
        <div className="text-sm font-medium text-neutral-900">{title}</div>
        <div className="text-xs text-neutral-500">{sub}</div>
      </div>
      <div className="inline-flex items-center gap-3">
        <button
          type="button"
          aria-label={`Decrease ${title}`}
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
          className="w-8 h-8 rounded-full border border-neutral-400 text-neutral-700 grid place-items-center disabled:border-neutral-200 disabled:text-neutral-300 disabled:cursor-not-allowed hover:border-neutral-900 hover:text-neutral-900"
        >
          −
        </button>
        <span className="text-sm tabular-nums min-w-[1.25rem] text-center">{value}</span>
        <button
          type="button"
          aria-label={`Increase ${title}`}
          onClick={() => onChange(value + 1)}
          className="w-8 h-8 rounded-full border border-neutral-400 text-neutral-700 grid place-items-center hover:border-neutral-900 hover:text-neutral-900"
        >
          +
        </button>
      </div>
    </div>
  );
}
